import React, { useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import { X, Upload, FileSpreadsheet, Calendar as CalendarIcon, Search } from "lucide-react";
import { toast } from "sonner";

const FREQ_LABEL = { monthly: "Mensual", bimonthly: "Bimestral", quarterly: "Trimestral", semiannual: "Semestral", annual: "Anual" };
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/**
 * Two-step Excel import: upload → preview with per-customer date picker → commit.
 */
export default function ImportExcelModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkDate, setBulkDate] = useState("");
  const [query, setQuery] = useState("");

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setRows([]);
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commit", "false");
      const { data } = await api.post("/customers/import-excel", fd);
      setPreview(data);
      setRows(data.rows.map((r) => ({ ...r })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const doCommit = async () => {
    if (!rows.length) return;
    const withoutDate = rows.filter((r) => !r.next_service_date).length;
    const msg = withoutDate
      ? `Se importarán ${rows.length} clientes. ${withoutDate} sin fecha de primera visita (usarán el próximo ciclo por defecto). ¿Continuar?`
      : `Se importarán ${rows.length} clientes. ¿Confirmar?`;
    if (!window.confirm(msg)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/customers/import-commit", { rows });
      toast.success(`${data.created} creados · ${data.updated} actualizados`);
      onImported();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const patch = (idx, patch) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const applyBulkDate = () => {
    if (!bulkDate) return;
    setRows((prev) => prev.map((r) => ({ ...r, next_service_date: r.next_service_date || bulkDate })));
    toast.success("Fecha aplicada a los clientes sin fecha");
  };

  const applyBulkDateAll = () => {
    if (!bulkDate) return;
    if (!window.confirm(`¿Sobrescribir la fecha de TODOS los ${rows.length} clientes?`)) return;
    setRows((prev) => prev.map((r) => ({ ...r, next_service_date: bulkDate })));
  };

  const filteredIndexes = useMemo(() => {
    if (!query.trim()) return rows.map((_, i) => i);
    const q = query.trim().toLowerCase();
    return rows.map((r, i) => i).filter((i) => (rows[i].name || "").toLowerCase().includes(q) || (rows[i].rut || "").includes(q));
  }, [query, rows]);

  const missingDates = rows.filter((r) => !r.next_service_date).length;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="import-excel-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-6xl border-t-4 border-[#FF5A00] max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Importación masiva</div>
            <div className="font-display font-bold text-xl">Importar clientes desde Excel</div>
          </div>
          <button onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 flex-1 overflow-auto">
          {!preview && (
            <>
              <div className="text-sm text-neutral-600 mb-4">
                <p>Columnas esperadas: <b>Contrato</b> (nombre), <b>Contrato</b> (Mensual/Bimestral), <b>Valor UF + IVA</b>, <b>RUT</b>, <b>Dirección</b>.</p>
                <p className="text-xs text-neutral-500 mt-1">Si arriba de esas columnas hay una fila con meses (Ene, Feb, Mar…), buscamos la primera columna con número y sugerimos esa fecha como primera visita. Podés ajustarlas antes de confirmar.</p>
              </div>
              <div className="border-2 border-dashed border-neutral-300 p-6 text-center bg-neutral-50">
                <FileSpreadsheet className="w-8 h-8 mx-auto text-[#FF5A00] mb-2" />
                <label className="cursor-pointer">
                  <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="hidden" data-testid="import-excel-file-input" />
                  <span className="font-mono-kpi text-xs uppercase tracking-[0.15em] text-[#FF5A00] hover:underline">
                    {file ? file.name : "Seleccionar archivo .xlsx"}
                  </span>
                </label>
                {file && (
                  <div className="mt-4">
                    <SecondaryButton onClick={doPreview} disabled={loading} data-testid="import-excel-preview">
                      {loading ? "Analizando…" : "Vista previa"}
                    </SecondaryButton>
                  </div>
                )}
              </div>
            </>
          )}

          {preview && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <div className="font-display font-bold text-base">Ajusta la primera visita ({rows.length} clientes)</div>
                  {missingDates > 0 && (
                    <div className="text-xs text-[#B28C00]">{missingDates} cliente(s) sin fecha detectada — asígnalos abajo.</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-neutral-400" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre o RUT" className="pl-7 pr-3 py-1.5 border border-neutral-300 text-xs w-52" data-testid="import-search" />
                  </div>
                  <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} className="px-2 py-1.5 border border-neutral-300 text-xs" data-testid="import-bulk-date" />
                  <SecondaryButton onClick={applyBulkDate} disabled={!bulkDate} data-testid="import-apply-missing">
                    Aplicar a sin fecha
                  </SecondaryButton>
                  <SecondaryButton onClick={applyBulkDateAll} disabled={!bulkDate} data-testid="import-apply-all">
                    Aplicar a todos
                  </SecondaryButton>
                </div>
              </div>

              <div className="border border-neutral-200 max-h-[400px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-neutral-100 z-10">
                    <tr>
                      <th className="text-left p-2 border-b border-neutral-200">Nombre</th>
                      <th className="text-left p-2 border-b border-neutral-200">RUT</th>
                      <th className="text-left p-2 border-b border-neutral-200">Frec.</th>
                      <th className="text-right p-2 border-b border-neutral-200">UF</th>
                      <th className="text-left p-2 border-b border-neutral-200">Dirección</th>
                      <th className="text-left p-2 border-b border-neutral-200"><CalendarIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Primera visita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIndexes.map((i) => {
                      const r = rows[i];
                      return (
                        <tr key={i} className={`border-b border-neutral-100 ${!r.next_service_date ? "bg-[#FFF5EC]" : ""}`} data-testid={`import-row-${i}`}>
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2 font-mono-kpi">{r.rut}</td>
                          <td className="p-2">{FREQ_LABEL[r.frequency] || r.frequency}</td>
                          <td className="p-2 text-right font-mono-kpi">
                            {r.requires_oc ? (
                              <span className="bg-[#FFC107]/10 text-[#B28C00] border border-[#FFC107]/40 px-2 py-0.5">OC</span>
                            ) : r.price_uf}
                          </td>
                          <td className="p-2 truncate max-w-[240px]">{r.address}</td>
                          <td className="p-2">
                            <input
                              type="date"
                              value={r.next_service_date || ""}
                              onChange={(e) => patch(i, { next_service_date: e.target.value })}
                              className={`px-2 py-1 border ${r.next_service_date ? "border-neutral-300" : "border-[#FF5A00]/60 bg-white"} focus:border-[#FF5A00] outline-none text-xs`}
                              data-testid={`import-date-${i}`}
                            />
                            {r.next_service_date && (
                              <div className="text-[10px] font-mono-kpi text-neutral-500 mt-0.5 uppercase">
                                {MONTHS[new Date(r.next_service_date).getMonth()]}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <div className="text-[10px] font-mono-kpi uppercase tracking-wider text-neutral-500">
            {preview ? `${rows.length - missingDates}/${rows.length} con fecha asignada` : "Selecciona el archivo Excel"}
          </div>
          <div className="flex items-center gap-2">
            <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
            {preview && (
              <PrimaryButton onClick={doCommit} disabled={loading} data-testid="import-excel-commit">
                <Upload className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                {loading ? "Importando…" : `Importar ${rows.length} clientes`}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
